import React, { useState, useEffect, useCallback } from 'react';
import { Megaphone, Calendar, List, BarChart3, Sparkles, Search, Plus, Image as ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import UpgradePrompt from './UpgradePrompt';
import PlatformConnections from './PlatformConnections';
import PostCalendar from './PostCalendar';
import PostList from './PostList';
import SocialAnalytics from './SocialAnalytics';
import AIContentGenerator from './AIContentGenerator';
import SocialImageGenerator from './SocialImageGenerator';
import BrandSwitcher from './components/BrandSwitcher';
import { getBrands } from '../../api/socialMedia';
import { SOCIAL_POST_DRAFT_SEED_KEY } from '../../services/SocialImageService';

const TABS = [
    { id: 'accounts', label: 'Accounts', icon: Megaphone },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'posts', label: 'Posts', icon: List },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'ai', label: 'AI Assistant', icon: Sparkles },
    { id: 'ai-images', label: 'AI Images', icon: ImageIcon },
];

const SocialMediaManager = ({
    userEmail,
    subscription,
    featureFlags = {},
    onUpgradePlan
}) => {
    const [activeTab, setActiveTab] = useState('accounts');
    const [searchQuery, setSearchQuery] = useState('');

    // Brand state
    const [currentBrandId, setCurrentBrandId] = useState(null);
    const [brandsLoading, setBrandsLoading] = useState(true);

    // Load brands on mount to get the initial brand
    useEffect(() => {
        const loadInitialBrand = async () => {
            try {
                console.log('[SocialMediaManager] Loading brands...');
                const response = await getBrands();
                console.log('[SocialMediaManager] Brands response:', response);

                if (response.brands?.length > 0) {
                    setCurrentBrandId(response.brands[0].id);
                    console.log('[SocialMediaManager] Set initial brand:', response.brands[0].id);
                } else {
                    console.log('[SocialMediaManager] No brands found - user needs to create one');
                }
            } catch (err) {
                console.error('[SocialMediaManager] Failed to load brands:', err);
            } finally {
                setBrandsLoading(false);
            }
        };

        if (featureFlags.hasSocialMedia) {
            loadInitialBrand();
        }
    }, [featureFlags.hasSocialMedia]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (window.sessionStorage.getItem(SOCIAL_POST_DRAFT_SEED_KEY)) {
            setActiveTab('posts');
        }
    }, []);

    // Handle brand change
    const handleBrandChange = useCallback((brandId) => {
        setCurrentBrandId(brandId);
    }, []);

    // Feature gate check
    if (!featureFlags.hasSocialMedia) {
        return <UpgradePrompt onUpgrade={onUpgradePlan} />;
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'accounts':
                return (
                    <PlatformConnections
                        subscription={subscription}
                        currentBrandId={currentBrandId}
                    />
                );
            case 'calendar':
                return <PostCalendar currentBrandId={currentBrandId} />;
            case 'posts':
                return <PostList currentBrandId={currentBrandId} searchQuery={searchQuery} />;
            case 'analytics':
                return <SocialAnalytics currentBrandId={currentBrandId} />;
            case 'ai':
                return <AIContentGenerator currentBrandId={currentBrandId} />;
            case 'ai-images':
                return (
                    <SocialImageGenerator
                        currentBrandId={currentBrandId}
                        onUseInPostRequest={() => setActiveTab('posts')}
                    />
                );
            default:
                return (
                    <PlatformConnections
                        subscription={subscription}
                        currentBrandId={currentBrandId}
                    />
                );
        }
    };

    return (
        <div className="min-h-full bg-[#f7f4ee] dark:bg-slate-950">
            {/* Hero - Compact version */}
            <div className="relative overflow-hidden px-6 py-6">
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(120deg, rgba(13, 148, 136, 0.98), rgba(245, 158, 11, 0.95))'
                    }}
                />
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.35), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.25), transparent 35%)'
                    }}
                />
                <div className="relative max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Title */}
                        <div className="text-white">
                            <h2 className="text-2xl font-semibold">Social Media</h2>
                            <p className="text-sm text-white/80 mt-1">
                                Manage accounts, schedule posts, and track performance.
                            </p>
                        </div>

                        {/* Controls */}
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Brand Switcher */}
                            <div className="w-full sm:w-auto sm:min-w-[200px]">
                                <BrandSwitcher
                                    currentBrandId={currentBrandId}
                                    onBrandChange={handleBrandChange}
                                    disabled={brandsLoading}
                                />
                            </div>

                            {/* Search */}
                            <div className="relative flex-1 sm:flex-none sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search posts..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 bg-white/95 dark:bg-slate-800 border-0 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white/50"
                                />
                            </div>

                            {/* Create Post Button */}
                            <button
                                onClick={() => setActiveTab('posts')}
                                className="px-4 py-2.5 bg-white text-slate-900 hover:bg-white/90 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                            >
                                <Plus className="w-4 h-4" />
                                Create Post
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="max-w-6xl mx-auto px-6 py-6">
                {/* Tabs */}
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 mb-6">
                    <div className="flex items-center gap-1 p-2 overflow-x-auto">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'bg-teal-600 text-white'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 min-h-[500px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={`${activeTab}-${currentBrandId}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            {renderTabContent()}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default SocialMediaManager;
